import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, FileText, ExternalLink } from "lucide-react";
import SEO from "@/components/SEO";
// @deprecated F12.1 — legacy Footer only on rollback (REACT_APP_HEADER_V4_ENABLED=false).
import Footer from "@/components/Footer";
import HeaderV4 from "@/components/header-v4/HeaderV4";
import FooterV4 from "@/components/footer-v4/FooterV4";

const V4_ENABLED = (process.env.REACT_APP_HEADER_V4_ENABLED ?? "true") !== "false";

const KNOWN_SLUGS = ["privacy", "terms", "cookies", "gdpr"];

const META = {
    privacy: {
        title: "Privacy Policy",
        desc: "How Global Lead Suite collects, stores and processes your personal data.",
    },
    terms: {
        title: "Terms of Service",
        desc: "The rules for using Global Lead Suite as a customer or free user.",
    },
    cookies: {
        title: "Cookie Policy",
        desc: "The cookies Global Lead Suite sets, why we set them and how to opt out.",
    },
    gdpr: {
        title: "GDPR Rights",
        desc: "Your rights under EU GDPR: access, portability, correction, deletion.",
    },
};

/**
 * Public legal page. Loads the corresponding markdown file from
 * /public/legal/{slug}.md at runtime and renders it. The templates are
 * EN-only by design — a visible disclaimer in the footer tells readers
 * to consult local counsel for their jurisdiction.
 */
export default function Legal() {
    const { slug } = useParams();
    const { t } = useTranslation("f13_pages");
    const [md, setMd] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (!KNOWN_SLUGS.includes(slug)) return;
        setLoading(true);
        setError(false);
        fetch(`/legal/${slug}.md`)
            .then((r) => {
                if (!r.ok) throw new Error("not_found");
                return r.text();
            })
            .then((txt) => setMd(txt))
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, [slug]);

    if (!KNOWN_SLUGS.includes(slug)) {
        return <Navigate to="/" replace />;
    }

    const meta = META[slug];

    return (
        <div className="min-h-screen bg-[var(--color-bg)] text-text-main">
            <SEO
                title={meta.title}
                description={meta.desc}
                canonical={`/legal/${slug}`}
                noindex={slug === "privacy" || slug === "terms"}
            />
            {V4_ENABLED ? (
                <HeaderV4 />
            ) : (
                <header className="sticky top-0 z-30 border-b border-[var(--color-border)] bg-[var(--color-bg)]/85 backdrop-blur-xl">
                    <div className="mx-auto max-w-4xl px-6 py-4 flex items-center justify-between">
                        <Link
                            to="/"
                            data-testid="legal-logo"
                            className="flex items-center gap-2 group"
                        >
                            <span className="h-7 w-7 rounded-md bg-white hover:bg-[#EDEDED] grid place-items-center text-black font-semibold text-sm">
                                G
                            </span>
                            <span className="text-sm font-medium">Global Lead Suite</span>
                        </Link>
                        <Link
                            to="/"
                            data-testid="legal-back"
                            className="text-xs text-text-secondary hover:text-text-main flex items-center gap-1.5 transition-colors"
                        >
                            <ArrowLeft className="h-3.5 w-3.5" />
                            {t("legal_wrapper.back_to_home")}
                        </Link>
                    </div>
                </header>
            )}

            <main
                data-testid={`legal-${slug}`}
                className="mx-auto max-w-3xl px-6 py-16"
            >
                <div className="mb-8 flex items-center gap-3">
                    <span className="rounded-full border border-[var(--color-border)] bg-[var(--color-card)] p-2">
                        <FileText className="h-4 w-4 text-[var(--color-secondary)]" />
                    </span>
                    <span className="text-xs uppercase tracking-widest text-text-secondary">
                        {t("legal_wrapper.eyebrow")}
                    </span>
                </div>

                {loading && (
                    <div className="text-sm text-text-secondary">{t("legal_wrapper.loading")}</div>
                )}
                {error && (
                    <div className="text-sm text-[var(--color-alert)]">
                        {t("legal_wrapper.error")}
                    </div>
                )}
                {!loading && !error && (
                    <article
                        data-testid={`legal-content-${slug}`}
                        className="prose prose-invert prose-headings:text-text-main prose-p:text-text-secondary prose-a:text-[var(--color-secondary)] prose-strong:text-text-main prose-code:text-[var(--color-secondary)] max-w-none"
                    >
                        <ReactMarkdown>{md}</ReactMarkdown>
                    </article>
                )}

                <div
                    data-testid="legal-disclaimer"
                    className="mt-16 rounded-2xl border border-[var(--color-border)] bg-[var(--color-panel-ai)] px-5 py-4 flex items-start gap-3"
                >
                    <ExternalLink className="h-4 w-4 text-[var(--color-secondary)] mt-0.5 shrink-0" />
                    <p className="text-xs text-text-secondary leading-relaxed">
                        {t("legal_wrapper.disclaimer")}
                    </p>
                </div>
            </main>

            {V4_ENABLED ? <FooterV4 /> : <Footer />}
        </div>
    );
}
